<template>
  <q-page class="home-page">
    <!-- Navigation Links -->
    <header class="header">
      <div class="nav-links">
        <a href="#features">{{ $t('home.nav.features') }}</a>
        <a href="#benefits">{{ $t('home.nav.benefits') }}</a>
        <a href="#industries">{{ $t('home.nav.industries') }}</a>
        <a href="#plans">{{ $t('home.nav.pricing') }}</a>
        <a href="#testimonials">{{ $t('home.nav.testimonials') }}</a>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <h1 style="text-align: center;">{{ $t('home.hero.title') }}</h1>
        <p style="text-align: center;">{{ $t('home.hero.subtitle') }}</p>
        <q-btn
          color="primary"
          size="lg"
          :label="$t('home.hero.cta')"
          @click="scrollToSection('plans')"
        />
      </div>
      <div class="hero-image">
        <img src="@/assets/hero-image.png" alt="Platform Preview" />
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features section">
      <h2 style="text-align: center;">{{ $t('home.features.title') }}</h2>
      <div class="grid-container">
        <div v-for="feature in features" :key="feature.id" class="card feature-card">
          <q-icon :name="feature.icon" size="48px" :color="feature.color" />
          <h3>{{ $t(`home.features.${feature.id}.title`) }}</h3>
          <p>{{ $t(`home.features.${feature.id}.description`) }}</p>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section id="benefits" class="benefits section">
      <h2 style="text-align: center;">{{ $t('home.benefits.title') }}</h2>
      <div class="grid-container">
        <div v-for="benefit in benefits" :key="benefit.id" class="card benefit-card">
          <h3>{{ $t(`home.benefits.${benefit.id}.title`) }}</h3>
          <p>{{ $t(`home.benefits.${benefit.id}.description`) }}</p>
        </div>
      </div>
    </section>

    <!-- Industries Section -->
    <section id="industries" class="industries section">
      <h2 style="text-align: center;">{{ $t('home.industries.title') }}</h2>
      <div class="grid-container">
        <div v-for="industry in industries" :key="industry.id" class="card industry-card">
          <q-icon :name="industry.icon" size="36px" />
          <h3>{{ $t(`home.industries.${industry.id}.title`) }}</h3>
          <p>{{ $t(`home.industries.${industry.id}.description`) }}</p>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section id="testimonials" class="testimonials section">
    <h2 style="text-align: center;">{{ $t('home.testimonials.title') }}</h2>
    <q-carousel
    v-model="testimonialSlide"
    animated
    navigation
    infinite
    :autoplay="5000"
    arrows
    >
    <q-carousel-slide
      v-for="testimonial in testimonials"
      :key="testimonial.id"
      :name="testimonial.id"
    >
      <div class="testimonial-card">
        <img
          :src="testimonial.avatar"
          :alt="testimonial.name"
          class="testimonial-avatar"
        />
        <blockquote class="testimonial-quote">
          {{ $t(`home.testimonials.${testimonial.id}.quote`) }}
        </blockquote>
        <cite class="testimonial-cite">
          <strong>{{ $t(`home.testimonials.${testimonial.id}.name`) }}</strong><br />
          <span>{{ $t(`home.testimonials.${testimonial.id}.title`) }}</span>
        </cite>
      </div>
    </q-carousel-slide>
  </q-carousel>
</section>

    <!-- Pricing Section -->
    <section id="plans" class="pricing section">
      <h2 style="text-align: center;">{{ $t('home.pricing.title') }}</h2>
      <div class="grid-container">
        <div v-for="plan in plans" :key="plan.id" class="card pricing-card">
          <h3>{{ $t(`home.pricing.${plan.id}.name`) }}</h3>
          <div class="plan-price">
            <span class="currency">$</span>
            <span class="amount">{{ plan.price }}</span>
            <span class="period">/{{ $t('home.pricing.period') }}</span>
          </div>
          <ul class="plan-features">
            <li v-for="feature in plan.features" :key="feature">
              <q-icon name="check" color="positive" />
              {{ $t(`home.pricing.${plan.id}.features.${feature}`) }}
            </li>
          </ul>
          <q-btn
            :color="plan.id === 'elite' ? 'primary' : 'secondary'"
            :label="$t('home.pricing.selectPlan')"
            class="full-width"
            @click="selectPlan(plan)"
          />
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>{{ $t('home.footer.company') }}</h4>
          <ul>
            <li><a href="#">{{ $t('home.footer.about') }}</a></li>
            <li><a href="#">{{ $t('home.footer.contact') }}</a></li>
            <li><a href="#">{{ $t('home.footer.careers') }}</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>{{ $t('home.footer.resources') }}</h4>
          <ul>
            <li><a href="#">{{ $t('home.footer.documentation') }}</a></li>
            <li><a href="#">{{ $t('home.footer.api') }}</a></li>
            <li><a href="#">{{ $t('home.footer.support') }}</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>{{ $t('home.footer.legal') }}</h4>
          <ul>
            <li><a href="#">{{ $t('home.footer.privacy') }}</a></li>
            <li><a href="#">{{ $t('home.footer.terms') }}</a></li>
            <li><a href="#">{{ $t('home.footer.security') }}</a></li>
          </ul>
        </div>
      </div>
    </footer>
  </q-page>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { Dialog } from 'quasar';
import LanguageSelector from '../components/LanguageSelector.vue';
import { useAuthStore } from '../stores/auth';
import { usePaymentStore } from '../stores/paymentStore';
import { useSubscriptionStore } from '../stores/subscription';
import { IntegrationService } from '../services/integrations';
import { getAuth } from 'firebase/auth';

const $q = useQuasar(); // Initialize Quasar instance for Notify and Dialog
const router = useRouter(); // Router instance for navigation

const authStore = useAuthStore(); // Authentication store
const paymentStore = usePaymentStore(); // Payment store
const subscriptionStore = useSubscriptionStore(); // Subscription store
const integrationService = new IntegrationService(); // Integration service for payments

// Testimonial carousel state
const testimonialSlide = ref(1);

// Static feature data
const features = [
  { id: 'voice', icon: 'phone', color: 'primary' },
  { id: 'chat', icon: 'chat', color: 'secondary' },
  { id: 'analytics', icon: 'analytics', color: 'accent' },
  { id: 'quality', icon: 'verified', color: 'positive' },
  { id: 'security', icon: 'security', color: 'negative' },
  { id: 'integration', icon: 'extension', color: 'info' },
];

// Static benefits data
const benefits = [
  { id: 'efficiency' },
  { id: 'scalability' },
  { id: 'security' },
  { id: 'support' },
];

// Static industries data
const industries = [
  { id: 'healthcare', icon: 'local_hospital' },
  { id: 'finance', icon: 'account_balance' },
  { id: 'retail', icon: 'shopping_cart' },
  { id: 'technology', icon: 'computer' },
  { id: 'education', icon: 'school' },
  { id: 'government', icon: 'account_balance' },
];

// Static testimonials data
const testimonials = [
  { id: 1, avatar: '/avatars/testimonial1.jpg' },
  { id: 2, avatar: '/avatars/testimonial2.jpg' },
  { id: 3, avatar: '/avatars/testimonial3.jpg' },
];

// Static subscription plans
const plans = [
  {
    id: 'pro',
    price: 99,
    features: ['feature1', 'feature2', 'feature3', 'feature4'],
  },
  {
    id: 'growth',
    price: 199,
    features: ['feature1', 'feature2', 'feature3', 'feature4', 'feature5', 'feature6'],
  },
  {
    id: 'elite',
    price: 299,
    features: ['feature1', 'feature2', 'feature3', 'feature4', 'feature5', 'feature6', 'feature7', 'feature8'],
  },
];

// Navigate to login page
// const navigateToLogin = () => {
//   try {
//     router.push('/auth/login'); // Ensure this matches your router configuration
//     console.log('Navigating to login...');
//   } catch (error) {
//     console.error('Error navigating to login:', error);
//   }
// };
const navigateToLogin = () => {
  try {
    router.push({ name: 'login' });
  } catch (error) {
    console.error('Named route "login" not found, redirecting to fallback path.', error);
    router.push('/auth/login'); // Direct fallback path
  }
};

const navigateToContactCenter = () => {
  router.push({ name: 'contact-center' });
};

const navigateToDialer = () => {
  router.push({ name: 'dialer' });
};

// Smooth scroll to a section
const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth' });
  } else {
    console.error(`Section with id "${sectionId}" not found.`);
  }
};

// Handle subscription plan selection
const selectPlan = async (plan) => {
  try {
    const selectedProcessor = paymentStore.selectedProcessor; // Get processor from paymentStore

    if (!selectedProcessor) {
      throw new Error('No payment processor is selected.');
    }

    $q.notify({ type: 'info', message: `Processing payment with ${selectedProcessor}` });

    let response;
    if (selectedProcessor === 'stripe') {
      response = await paymentStore.createStripePayment({
        planId: plan.id,
        amount: plan.price * 100,
        description: `${plan.name} subscription`,
      });
    } else if (selectedProcessor === 'paypal') {
      response = await paymentStore.createPaypalOrder({
        planId: plan.id,
        amount: plan.price,
      });
    }

    if (response.success) {
      $q.notify({ type: 'positive', message: 'Payment successful!' });
    } else {
      throw new Error('Payment failed.');
    }
  } catch (error) {
    $q.notify({ type: 'negative', message: error.message || 'Payment processing failed.' });
  }
};

// Fetch subscriptions on mount
// onMounted(async () => {
//   try {
//     const auth = getAuth();
//     const user = auth.currentUser;

//     if (!user) {
//       console.log('No user authenticated. Skipping payment and subscription fetch.');
//       return; // Ensure this doesn't interfere with navigation
//     }

//     await paymentStore.fetchPayments();
//     await paymentStore.fetchSubscriptions();
//     $q.notify({ type: 'info', message: 'Data loaded successfully.' });
//   } catch (error) {
//     console.error('Error during initialization:', error);
//     $q.notify({ type: 'negative', message: 'Error loading data.' });
//   }
// });
onMounted(() => {
  console.log('Home page loaded. No authentication required.');
});
</script>

<style scoped>
/* General Section Styling */
.section {
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.q-page {
  background-color: white; /* Set the default background color */
  color: black; /* Ensure text color is visible */
  padding: 70px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card h3 {
  font-size: 1.25rem; /* Adjust font size for better fit */
  margin: 1rem 0 0.5rem; /* Add spacing between title and description */
  line-height: 1.4; /* Ensure readable line height */
}

.card p {
  font-size: 1rem; /* Slightly smaller font for descriptions */
  margin: 0;
  color: #555; /* Muted text color for better readability */
  line-height: 1.6; /* Ensure content wraps neatly */
}

.card q-icon {
  margin-bottom: 1rem; /* Add space below the icon */
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
}

/* Header */
.header {
  position: fixed;
  top: 2rem;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
}

.nav-links {
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  font-weight: bold;
  color: black;
  transition: color 0.3s ease;
  display: flex;
  gap: 2rem;
  padding: 1rem;
}

.nav-links a {
  text-decoration: none;
  font-weight: bold;
  color: black;
  padding: 0.5rem 1rem;
  transition: color 0.3s ease;
}

.nav-links a:hover {
  color: #007bff;
}

/* Hero Section */
.hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  background: linear-gradient(135deg, var(--q-primary), var(--q-secondary));
  color: white;
  padding: 6rem 2rem;
}

.hero-content {
  max-width: 600px;
}

.hero-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .hero {
    flex-direction: column;
  }

  .hero-image {
    margin-top: 2rem;
  }
}

/* Features Section */
.features .grid-container {
  background: #f9f9f9;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  align-items: stretch;
}

/* Benefits Section */
.benefits .grid-container {
  background: #ffffff;
}

/* Industries Section */
.industries .grid-container {
  background: #f5f5f5;
}

/* Pricing Section */
.pricing {
  background: #f9f9f9;
}

.pricing-card {
  padding: 2rem;
  text-align: center;
  border: 1px solid #ddd;
}

.pricing-card .plan-price {
  font-size: 1.5rem;
  margin: 1rem 0;
}

.plan-price .currency {
  font-size: 1rem;
}

.plan-price .amount {
  font-size: 2rem;
  font-weight: bold;
}

.plan-price .period {
  font-size: 1rem;
  color: gray;
}

.pricing-card ul {
  list-style: none;
  padding: 0;
  margin: 1rem 0;
}

.pricing-card ul li {
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pricing-card ul li q-icon {
  margin-right: 0.5rem;
}

/* Testimonials Section */
.testimonials {
  background: linear-gradient(135deg, var(--q-primary), var(--q-secondary));
  color: white;
  padding: 4rem 2rem;
  border-radius: 8px;
}

.testimonial-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.testimonial-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 1rem;
  object-fit: cover;
}

.testimonial-card {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.testimonial-quote {
  font-size: 1.2rem;
  font-style: italic;
  line-height: 1.6;
  color: #333;
}

.testimonials .q-carousel__control {
  background-color: #007bff !important; /* Apply primary color */
}

/* Footer */
.footer {
  background: #f5f5f5;
  padding: 2rem;
  text-align: center;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  justify-items: center;
}

.footer-section h4 {
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.footer-section ul {
  list-style: none;
  padding: 0;
}

.footer-section ul li {
  margin-bottom: 0.5rem;
}

.footer-section ul li a {
  text-decoration: none;
  color: var(--q-primary);
}

.footer-section ul li a:hover {
  color: var(--q-secondary);
}

.social-links a {
  font-size: 1.5rem;
  margin: 0 0.5rem;
  color: var(--q-primary);
  transition: color 0.3s ease;
}

.social-links a:hover {
  color: var(--q-secondary);
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
  }
}
</style>